/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Observable, BehaviorSubject } from 'rxjs';
import { last } from 'rxjs/operators';
import { fastSplice } from 'utils/basic-utils';

// - responsible for storing the undo cache
// - the public api should be _extremely_ simple
// - the details of where/ how the data will be stored should be largely abstracted away. It should be assumed that this uses an
// async request system.
// - the intial version will store everything in memory. A subsequent version will also incorporate local storage and potentially
// webworkers. Keep both versions around, create an UndoCacheInterface and have both an InMemoryUndoCache and IndexedDbUndoCache. That can
// be chosen when setting up the [[UndoService]].
// - should always have a skeleton of the data in memory which points to all the actual data and can be queried to see metadata
// about the cache.

// this one feels like it should be a singleton and not within angular (angular would get us route information). This may not even want to
//be a singleton and instead just be instantiated by things that want to access a cache
// the cache concept can probably exist more generically (maybe have an interface and several cache styles. In memory, local storage, etc.)

//misc thoughts
// - don't use localStorage, indexedDB or a library wrapping it is the way to go
// - when implementing in non-singleton version, the in-memory version will differ from indexedDb in that instances pointing at the same
// place will not access the same set of data. This can be added later with a shared in-memory layer.

//temp source to get things working
const specialSeparator = '-';

//this being an array is not workable overall, but should work to get something working here.
//lmaybesplit into object/ array based versions in the same manner as firebase?
// In theory, creating an object here would result in it being shared among all in memory caches
//const inMemoryCache:Array<any> = [];
// window.inMemoryCache = inMemoryCache;

export class UndoCache {
	public inMemoryCache: any[] = [];
	//path is delibrately not used but still passed in for when the shared in-memory cache is added (see undo-cache.service-old-ver.ts)
	constructor(private path: string[]) {}

	//every write method needs to next this (only for the in memory version most likely)
	public skeletonList$ = new BehaviorSubject(this.inMemoryCache);

	public push<T>(val: T) {
		this.baseRead()
			.pipe(last())
			.subscribe((list) => {
				list.unshift(val);
				this.skeletonList$.next(list);
			});
	}

	//likely want additional/ alternative more specific remove methods?
	public removePrevious(idx: number) {
		fastSplice(this.inMemoryCache, 0, idx + 1); //always from 0 to idx... so the count is idx + 1
		this.skeletonList$.next(this.inMemoryCache);
	}

	//a single idx for the simple operation, multiple for efficiency
	public remove(idx: number | number[]) {
		if (typeof idx !== 'number') {
			const sortedList = idx.sort(); //safety
			for (let i = sortedList.length - 1; i >= 0; i--) {
				fastSplice(this.inMemoryCache, sortedList[i], 1);
			}
		} else {
			fastSplice(this.inMemoryCache, idx, 1);
		}
		this.skeletonList$.next(this.inMemoryCache);
	}

	//only updates the metadata, so it doesn't need to persist changes in the "main" cache (mainly applies to the indexedDb version)
	// need to more carefully consider this noEmit concept... maybe everything gets this, maybe nothing should...
	public metaUpdate(idx: number, data: any, noEmit = false) {
		if (idx < 0 || idx > this.inMemoryCache.length - 1) {
			return;
		}
		this.inMemoryCache[idx] = data;
		if (!noEmit) {
			this.skeletonList$.next(this.inMemoryCache);
		}
	}

	//currently playing with the idea that this will return an observable. It will emit as many times as necessary, then complete once it
	//has all the data. In the current in memory version it will just emit once, then complete.
	//This will be how the "actual" data will be gathered from the skeleton list
	public baseRead<T = any>(index?: number) {
		return new Observable<T>((subscriber) => {
			let completed = false;
			function done(val) {
				subscriber.next(val);
				subscriber.complete();
				completed = true;
				return;
			}

			if (index === undefined) {
				done(this.inMemoryCache);
				return;
			}
			done(this.inMemoryCache[index]);
			return;
		});
	}

	public destroy() {
		this.skeletonList$.complete();
	}
}
